import { useRef, type ComponentProps, type ReactNode } from "react";
import type { ProviderInteractionMode, RuntimeMode } from "@cmmd-forge/contracts";
import { CloudIcon, MonitorIcon, ChevronDownIcon, XIcon } from "lucide-react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { ComposerFooterModeControls } from "../chat/ComposerFooterModeControls";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { BranchChip, ExecutionChip, ProjectChip, WorktreeChip } from "./composerChips";
import { STRIP_CONTROL_CLASS } from "../chat/composerControlsStripChrome";
import { WORKTREE_MODE_LABELS } from "./NewThreadLanding.logic";

type ExecutionProps = ComponentProps<typeof ExecutionChip>;
type ProjectProps = ComponentProps<typeof ProjectChip>;
type WorktreeProps = ComponentProps<typeof WorktreeChip>;
type BranchProps = ComponentProps<typeof BranchChip>;

export interface NewThreadMobileSetupProps {
  readonly execution: ExecutionProps;
  readonly project: ProjectProps;
  readonly worktree: WorktreeProps;
  readonly branch: BranchProps;
  readonly interactionMode: ProviderInteractionMode;
  readonly runtimeMode: RuntimeMode;
  readonly onToggleInteractionMode: () => void;
  readonly onRuntimeModeChange: (mode: RuntimeMode) => void;
}

interface SetupRowProps {
  readonly label: string;
  readonly children: ReactNode;
}

const rowTriggerClass =
  "w-full min-w-0 justify-start rounded-md bg-transparent px-2 text-foreground hover:bg-accent [&>span]:max-w-none";

function SetupRow(props: SetupRowProps) {
  return (
    <div className="grid min-w-0 grid-cols-[6rem_minmax(0,1fr)] items-center gap-2 px-1">
      <span className="px-2 font-medium text-muted-foreground text-sm">{props.label}</span>
      <div className="min-w-0">{props.children}</div>
    </div>
  );
}

function MobileSetupSummary(
  props: Pick<NewThreadMobileSetupProps, "execution" | "project" | "worktree">,
) {
  const selectedExecution =
    props.execution.options.find((option) => option.id === props.execution.selectedId) ??
    props.execution.options[0] ??
    null;
  const projectLabel = props.project.activeProject?.name ?? "Choose a project";
  const setupSummary = `${selectedExecution?.label ?? "Choose runtime"} · ${WORKTREE_MODE_LABELS[props.worktree.mode]}`;
  const RuntimeIcon = selectedExecution?.kind === "cloud" ? CloudIcon : MonitorIcon;

  return (
    <DialogTrigger
      render={
        <button
          type="button"
          className={`${STRIP_CONTROL_CLASS} flex min-w-0 flex-1 shrink items-center text-left text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset`}
          aria-label={`Configure task: ${projectLabel}`}
        />
      }
    >
      <RuntimeIcon className="size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 truncate">{setupSummary}</span>
      <ChevronDownIcon className="size-3 shrink-0" aria-hidden="true" />
    </DialogTrigger>
  );
}

function MobileSetupFields(props: NewThreadMobileSetupProps) {
  return (
    <div className="grid gap-4">
      <section className="grid gap-2" aria-labelledby="new-thread-context-heading">
        <h3 id="new-thread-context-heading" className="px-1 font-semibold text-foreground text-sm">
          Context
        </h3>
        <div className="divide-y divide-border overflow-hidden rounded-lg border border-border">
          <SetupRow label="Run on">
            <ExecutionChip {...props.execution} triggerClassName={rowTriggerClass} />
          </SetupRow>
          <SetupRow label="Workspace">
            <ProjectChip {...props.project} triggerClassName={rowTriggerClass} />
          </SetupRow>
          <SetupRow label="Start in">
            <WorktreeChip {...props.worktree} triggerClassName={rowTriggerClass} />
          </SetupRow>
          <SetupRow label="Branch">
            <BranchChip {...props.branch} triggerClassName={rowTriggerClass} />
          </SetupRow>
        </div>
      </section>

      <section className="grid gap-2" aria-labelledby="new-thread-behavior-heading">
        <h3 id="new-thread-behavior-heading" className="px-1 font-semibold text-foreground text-sm">
          Behavior
        </h3>
        <div
          aria-label="Task behavior"
          className="grid grid-cols-2 divide-x divide-border overflow-hidden rounded-lg border border-border [&>button]:w-full [&>button]:justify-start [&>button]:rounded-none [&>button]:bg-transparent [&>button]:px-3 [&>button]:text-foreground [&>button]:hover:bg-accent [&_[data-slot=select-trigger]]:w-full [&_[data-slot=select-trigger]]:justify-start [&_[data-slot=select-trigger]]:rounded-none [&_[data-slot=select-trigger]]:bg-transparent [&_[data-slot=select-trigger]]:px-3 [&_[data-slot=select-trigger]]:text-foreground [&_[data-slot=select-trigger]]:hover:bg-accent"
        >
          <ComposerFooterModeControls
            interactionMode={props.interactionMode}
            runtimeMode={props.runtimeMode}
            showInteractionLabelOnMobile
            showSeparators={false}
            onToggleInteractionMode={props.onToggleInteractionMode}
            onRuntimeModeChange={props.onRuntimeModeChange}
          />
        </div>
      </section>
    </div>
  );
}

export function NewThreadMobileSetup(props: NewThreadMobileSetupProps) {
  const popupRef = useRef<HTMLDivElement | null>(null);
  const isDesktop = useMediaQuery("md");

  if (isDesktop) return null;

  return (
    <div
      aria-label="Mobile task setup"
      className="flex w-full min-w-0 items-center gap-2 md:hidden"
    >
      <Dialog>
        <MobileSetupSummary {...props} />
        <DialogPopup
          ref={popupRef}
          className="max-h-[calc(100dvh-3rem)] outline-none md:hidden"
          initialFocus={(openType) => (openType === "keyboard" ? true : popupRef.current)}
          showCloseButton={false}
        >
          <DialogClose
            aria-label="Close"
            className="absolute end-0.5 top-0.5 z-10 inline-flex size-touch touch-manipulation items-center justify-center rounded-md text-muted-foreground outline-none hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            <XIcon className="size-4" aria-hidden="true" />
          </DialogClose>
          <DialogHeader className="px-4 pt-4 pr-14 pb-2">
            <DialogTitle>Task setup</DialogTitle>
            <DialogDescription className="sr-only">
              Choose where Forge works and how much autonomy it has.
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="px-3 pt-1 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            <MobileSetupFields {...props} />
          </DialogPanel>
        </DialogPopup>
      </Dialog>
      <div className="max-w-[45%] min-w-0 shrink">
        <BranchChip
          {...props.branch}
          triggerClassName={`${STRIP_CONTROL_CLASS} w-full min-w-0 justify-end [&>span]:min-w-0`}
        />
      </div>
    </div>
  );
}
